import { defineAsyncComponent, reactive, toRaw } from "vue";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { createLoading } from "../../../../utils/loading";
import { Col, Modal, Row } from "ant-design-vue";
import { has } from "../../../../utils/menu-context";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import { createAppPageHeaderTop, createPage } from "../../../../utils/page";
import { required, validateError } from "../../../../utils/validate";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickArchive,
    onClickCreate,
    onClickDelete,
    onClickEdit,
    onClickView
} from "../../../../utils/form";
import {
    createFieldInputNumber,
    /*
    createFieldDatetime,
*/
    createFieldSelect
} from '../../../../components/input/template-builder/template-builder.logic';
import lodash from "lodash";
import { modalStyleForm } from "../../../../utils/constant";
import { filter } from "../../../../utils/search";

export const { isLoading, setIsLoading } = createLoading()

export const actionPrefix = "resource/v1/transaction/samplingProductAnalysis";
export const routerPrefix = 'sampling-product-analysis-cross-centers'

const AnalysisCrossCenterForm = defineAsyncComponent(() => import('./AnalysisCrossCenterForm'))

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form })
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria })
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id })
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id })
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } })
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids })
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids })
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } })
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria })
export const callExtCenterTypes = () => store.dispatch('resource/v1/master/lookup/cacheFindAllByGroup', { group: 'EXT_CENTER_TYPE' })

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true)
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then((record) => onClickView({ record, routerPrefix }))
        })
        .catch((error) => validateError({ error }));
}

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef) }))
        .catch((error) => {
            validateError({ error })
            throw error
        });
}

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ['**'],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
        }
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: 'id', type: 'UUID', operator: '=', value: id }],
            children: [],
        }
    }
}

export const openModalForm = ({ props, callback }) => {
    const refs = reactive({ form: null })
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            width: '800px',
            okText: i18n.global.t('button.create'),
            okButtonProps: { disabled: props.viewOnly },
            onOk: () => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.save") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        refs.form.callOnClickModalOk()
                            .then((record) => {
                                callback(record);
                                options.bind.onCancel()
                            })
                    },
                });
            }
        }),
        render: () => <AnalysisCrossCenterForm ref={(e) => { if (e) refs.form = e }} {...props}></AnalysisCrossCenterForm>
    })
    return options
}

export const createForm = () => {
    return {
        refSamplingProductAnalysis: null,
        refDepartment: null,
        refExtCenterType: null,
        samplingAmount: null,
        crossCenterRequestDatetime: null,
        topics: [],
    }
}

export const createRules = () => {
    return {
        refSamplingProductAnalysis: [required],
        refDepartment: [required],
        refExtCenterType: [required],
        samplingAmount: [required],
        topics: [required],
    }
}

export const createField = ({ props, data }) => {
    const { viewOnly, refLabTypeKey } = props
    const useForm = data.useForm
    const modelRef = useForm.modelRef
    return [
        createFieldSelect({
            key: 'refSamplingProductAnalysis',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
            label: () => i18n.global.t('model.samplingProduct.samplingNo'),
            inputProp: () => ({
                value: modelRef.refSamplingProductAnalysis,
                'onUpdate:value': value => {
                    modelRef.refSamplingProductAnalysis = value
                    modelRef.samplingAmount = modelRef.refSamplingProductAnalysis.samplingAmount
                    if (!value) {
                        modelRef.refDepartment = null
                        modelRef.crossCenterRequestDatetime = null
                    }
                },
                list: ({ searchQuery }) => store.dispatch('resource/v1/transaction/samplingProductAnalysis/searchBySamplingNo', {
                    searchQuery,
                    where: [
                        { name: 'refLabType.key', type: 'String', operator: '=', value: refLabTypeKey },
                        { name: 'refExtCenterType.id', type: 'UUID', operator: '=', value: null },
                        { name: 'refLabAnalysisStatus.key', type: 'String', operator: '=', value: 'Wait' },
                        { name: 'refTransferFrom.id', type: 'UUID', operator: '=', value: null }
                    ],
                    children: [
                        {
                            name: 'topics',
                            select: ['id', 'refLabTopic.*'], page: 0, size: 10000,
                            where: [
                                { name: 'transferTos.id', type: 'UUID', operator: '=', value: null }
                            ]
                        }
                    ]
                }),
                optLabel: "refSamplingProduct.samplingNo",
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.id`) }
        }),
        createFieldInputNumber({
            visible: () => modelRef.refSamplingProductAnalysis != null,
            key: `samplingAmount`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.samplingCrossCenterAmount'),
            inputProp: () => ({
                value: modelRef.samplingAmount,
                'onUpdate:value': value => {
                    modelRef.samplingAmount = value
                },
            })
        }),
        createFieldSelect({
            visible: () => modelRef.refSamplingProductAnalysis != null,
            key: 'refDepartment',
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
            label: () => i18n.global.t('model.department.modelName'),
            inputProp: () => ({
                value: modelRef.refDepartment,
                'onUpdate:value': value => modelRef.refDepartment = value,
                list: ({ searchQuery }) => store.dispatch('resource/v1/master/department/cacheFindAll', {
                    searchQuery,
                    where: [
                        { name: 'refLabType.key', type: 'String', operator: '=', value: refLabTypeKey },
                        { name: 'id', type: 'UUID', isNot: true, operator: '=', value: modelRef.refSamplingProductAnalysis?.refDepartment?.id },
                    ]
                }),
                optLabel: "refParent.nameTh",
            }),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
        }),
        createFieldSelect({
            visible: () => modelRef.refSamplingProductAnalysis != null,
            key: `topics`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
            label: () => i18n.global.t('model.samplingProductAnalysis.labels.selectTopic'),
            inputProp: () => ({
                value: modelRef.topics,
                'onUpdate:value': (value) => {
                    modelRef.topics = value.filter( d => d.isDelete!=true)
                },
                disabled: viewOnly,
                list: ({ searchQuery }) => {
                    return filter({
                        list: modelRef.refSamplingProductAnalysis?.topics || [],
                        where: [{ name: 'refLabTopic.name', operator: 'like', type: "String", value: searchQuery }]
                    })
                },
                optKey: 'id',
                renderLabel: ({ item }) => {
                    return <span>{item?.refLabTopic.name}</span>
                },
                placeholder: i18n.global.t(`model.samplingProductAnalysis.labels.selectTopic`),
                mode: 'multiple',
            }),
        }),
    ]
}

export const createFormTitle = () => {
    return i18n.global.t('model.samplingProductAnalysis.labels.createCrossCenterAnalysis')
}

export const loadForm = ({ props, data }) => {
    setIsLoading(true)
    const { form } = props
    const { id, useForm } = data
    const { modelRef } = useForm
    let promise = null
    if (form) {
        promise = Promise.resolve({ data: [form] })
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) })
    } else {
        promise = Promise.resolve({ data: [modelRef] })
    }
    return promise
        .then(page => initUseForm({ data, page, routerPrefix }))
        .then(() => loadDefaultForm({ data }))
        .finally(() => setIsLoading(false))
}

export const loadDefaultForm = ({ data }) => {
    const promises = []
    promises.push(Promise.all([callExtCenterTypes()])
        .then(arr => {
            data.extCenterTypes = arr[0]
            const refExtCenterType = data.extCenterTypes?.find(d=>d?.key === 'Cross Center')
            data.form.refExtCenterType = refExtCenterType
        }))
    return Promise.all(promises)
}

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs })
    data.form = createForm();
    data.rules = createRules();
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs })
        .then(() => {
            if (props.setAppPageHeader) {
                setAppPageHeader({
                    slots: {
                        top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                        default: () => createAppPageHeaderDefault({ props, data, refs })
                    }
                });
            }
        })
}

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = []
    const col2 = []
    const { menuContext, viewOnly } = props
    const { id, isActive } = data.useForm.modelRef
    if (id && has({ menuContext, key: 'canCreate' })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && has({ menuContext, key: 'canView' })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && isActive && has({ menuContext, key: 'canEdit' })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: 'm-1' }))
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: 'm-1' }))
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: 'canActive' })) {
        col2.push(createButtonActive({ onClick: () => onClickActive({ record: { id }, callActive, setIsLoading: setIsLoadingApp, onClickEdit: () => onClickEdit({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canArchive' })) {
        col2.push(createButtonArchive({ onClick: () => onClickArchive({ record: { id }, callArchive, setIsLoading: setIsLoadingApp, onClickView: () => onClickView({ record: { id }, routerPrefix }) }), classStyle: 'm-1' }))
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: 'canDelete' })) {
        col2.push(createButtonDelete({ onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }), classStyle: 'm-1' }))
    }
    return <Row type="flex">
        <Col>{col1}</Col>
        <Col class="ml-auto">{col2}</Col>
    </Row>
}

export const state = reactive({
    page: createPage({ options: { select: ['**'] } }),
    tableOptions: { search: { activeKey: 'Search' } }
})
